import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { token_api } from "../../Utils/Network";

const cardSx = {
  background: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(16,24,40,.06), 0 1px 2px rgba(16,24,40,.08)",
  border: "1px solid #eef0f4",
  p: { xs: 2, md: 3 },
  mb: 3,
};

const groupLabel = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#8a90a6",
  margin: "0 0 12px",
};
const fieldTitle = { fontSize: 14, fontWeight: 600, color: "#313E6C" };
const fieldHint = { fontSize: 12, color: "#8a90a6", margin: "2px 0 8px" };
// Taller, rounded editors that line up side by side.
const editorSx = {
  "& .ql-toolbar": { borderRadius: "10px 10px 0 0", borderColor: "#dfe3ea" },
  "& .ql-container": {
    minHeight: 170,
    borderRadius: "0 0 10px 10px",
    borderColor: "#dfe3ea",
    fontSize: 14,
  },
};

const emptyConcern = {
  counselling_concerns_category: "",
  concern: "",
  name_for_report: "",
  description: "",
  practical_strategies: "",
  index: 1,
  for_report: true,
};

const unwrap = (res) =>
  res?.data?.data?.results || res?.data?.data || res?.data?.results || res?.data || [];

/**
 * The concerns a student can have and the text each one prints in the
 * report: its name, why it matters, and the practical strategies.
 */
const Concerns = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyConcern);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  // The Add Category popup, its error line, and the message after it closes.
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const [toast, setToast] = useState("");
  // The category being renamed in the popup; null when adding a new one.
  const [renamingCategory, setRenamingCategory] = useState(null);

  const openCategoryDialog = (cat = null) => {
    setRenamingCategory(cat);
    setNewCategory(cat ? cat.category : "");
    setCategoryError("");
    setCategoryDialogOpen(true);
  };

  const fetchCategories = () => {
    token_api
      .get("counseling/v1/counselling_concerns/")
      .then((res) => setCategories(unwrap(res)))
      .catch((err) => console.error("Failed to load concerns:", err));
  };

  useEffect(fetchCategories, []);

  const saveCategory = () => {
    const name = newCategory.trim();
    if (!name) {
      setCategoryError("Please type a name for the category.");
      return;
    }
    if (categories.some((c) => c.id !== renamingCategory?.id &&
        c.category.trim().toLowerCase() === name.toLowerCase())) {
      setCategoryError(`"${name}" already exists. Please use a different name.`);
      return;
    }
    if (renamingCategory) {
      if (name === renamingCategory.category) {
        setCategoryDialogOpen(false);
        return;
      }
      setAddingCategory(true);
      token_api
        .patch(`counseling/v1/counselling_concerns/${renamingCategory.id}/`, { category: name })
        .then(() => {
          setCategoryDialogOpen(false);
          setToast(`Category renamed to "${name}".`);
          fetchCategories();
        })
        .catch((err) => {
          console.error("Rename category error:", err?.response?.data || err);
          setCategoryError("Could not rename the category. Please try again.");
        })
        .finally(() => setAddingCategory(false));
      return;
    }
    setAddingCategory(true);
    setCategoryError("");
    token_api
      .post("counseling/v1/counselling_concerns/", { category: name })
      .then((res) => {
        const created = res?.data?.data || res?.data;
        setCategoryDialogOpen(false);
        setToast(`"${name}" added. Now add its concerns in the form above.`);
        // The new category lands at the bottom of the list, out of sight, so
        // pre-select it in the Add Concern form and take the user there.
        if (created?.id && !editingId) {
          setForm((f) => ({ ...f, counselling_concerns_category: created.id }));
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
        fetchCategories();
      })
      .catch((err) => {
        console.error("Add category error:", err?.response?.data || err);
        setCategoryError("Could not add the category. Please try again.");
      })
      .finally(() => setAddingCategory(false));
  };

  const deleteCategory = (cat) => {
    const count = (cat.concerns || []).length;
    const warning = count ? ` Its ${count} concern(s) will be deleted too.` : "";
    if (!window.confirm(`Delete category "${cat.category}"?${warning}`)) return;
    token_api
      .delete(`counseling/v1/counselling_concerns/${cat.id}/`)
      .then(fetchCategories)
      .catch((err) => {
        console.error("Delete category error:", err?.response?.data || err);
        alert("Failed to delete the category.");
      });
  };

  const saveConcern = () => {
    if (!form.counselling_concerns_category || !form.concern || !form.name_for_report) {
      alert("Category, concern and name for report are required.");
      return;
    }
    setSaving(true);
    const payload = { ...form, index: Number(form.index) || 1 };
    const request = editingId
      ? token_api.patch(`counseling/v1/counselling_concern_items/${editingId}/`, payload)
      : token_api.post("counseling/v1/counselling_concern_items/", payload);
    request
      .then(() => {
        cancelEdit();
        fetchCategories();
      })
      .catch((err) => {
        console.error("Save concern error:", err?.response?.data || err);
        alert("Failed to save the concern.");
      })
      .finally(() => setSaving(false));
  };

  const editConcern = (c) => {
    setEditingId(c.id);
    setForm({
      counselling_concerns_category: c.counselling_concerns_category,
      concern: c.concern || "",
      name_for_report: c.name_for_report || "",
      description: c.description || "",
      practical_strategies: c.practical_strategies || "",
      index: c.index ?? 1,
      for_report: !!c.for_report,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteConcern = (c) => {
    if (!window.confirm(`Delete concern "${c.concern}"?`)) return;
    token_api
      .delete(`counseling/v1/counselling_concern_items/${c.id}/`)
      .then(() => {
        if (editingId === c.id) cancelEdit();
        fetchCategories();
      })
      .catch((err) => {
        console.error("Delete concern error:", err?.response?.data || err);
        alert("Failed to delete the concern.");
      });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyConcern);
  };

  return (
    <div style={{ background: "#f5f7fb", minHeight: "100%", padding: "24px 0" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 16px" }}>
        <Box sx={cardSx}>
          <h2 style={{ color: "#313E6C", margin: "0 0 4px" }}>
            {editingId ? "Edit Concern" : "Add Concern"}
          </h2>
          <p style={{ color: "#696984", margin: "0 0 20px", fontSize: 14 }}>
            What you write here is what the student's report prints for this
            concern.
          </p>

          <div style={groupLabel}>Concern details</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Category *"
                size="small"
                fullWidth
                value={form.counselling_concerns_category}
                onChange={(e) =>
                  setForm({ ...form, counselling_concerns_category: e.target.value })
                }
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Concern *"
                size="small"
                fullWidth
                helperText="Shown on the website and in the counsellor panel"
                value={form.concern}
                onChange={(e) => setForm({ ...form, concern: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Name for Report *"
                size="small"
                fullWidth
                helperText="Printed in the PDF report"
                value={form.name_for_report}
                onChange={(e) => setForm({ ...form, name_for_report: e.target.value })}
              />
            </Grid>
            <Grid item xs={5} md={2}>
              <TextField
                label="Order"
                type="number"
                size="small"
                fullWidth
                helperText="Position in its category"
                value={form.index}
                onChange={(e) => setForm({ ...form, index: e.target.value })}
              />
            </Grid>
            <Grid item xs={7} md={4}>
              <FormControlLabel
                sx={{ ml: 0, mt: 0.5 }}
                control={
                  <Switch
                    checked={form.for_report}
                    onChange={(e) => setForm({ ...form, for_report: e.target.checked })}
                  />
                }
                label={<span style={{ fontSize: 14 }}>Show in report</span>}
              />
            </Grid>
          </Grid>

          <div style={{ ...groupLabel, marginTop: 24 }}>Report text</div>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div style={fieldTitle}>Why this matters</div>
              <div style={fieldHint}>
                Printed as “Why is it important to work on this?” in Transform reports.
              </div>
              <Box sx={editorSx}>
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(value) => setForm((f) => ({ ...f, description: value }))}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <div style={fieldTitle}>Practical strategies</div>
              <div style={fieldHint}>
                Printed as bullet points in the Well-Being Kit / Resources &amp; Strategies.
              </div>
              <Box sx={editorSx}>
                <ReactQuill
                  theme="snow"
                  value={form.practical_strategies}
                  onChange={(value) => setForm((f) => ({ ...f, practical_strategies: value }))}
                />
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: "1px solid #eef0f4",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            {editingId && (
              <Button variant="outlined" onClick={cancelEdit}>
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              onClick={saveConcern}
              disabled={saving}
              style={{ backgroundColor: "#313E6C", minWidth: 140 }}
            >
              {saving ? "Saving…" : editingId ? "Save Changes" : "Add Concern"}
            </Button>
          </Box>
        </Box>

        <Box sx={cardSx}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <h2 style={{ color: "#313E6C", margin: 0 }}>Concerns</h2>
            <Button variant="outlined" onClick={() => openCategoryDialog()}>
              + Add Category
            </Button>
          </div>

          <Dialog
            open={categoryDialogOpen}
            onClose={() => !addingCategory && setCategoryDialogOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ color: "#313E6C", fontWeight: 700 }}>
              {renamingCategory ? "Edit Category Name" : "Add a Concern Category"}
            </DialogTitle>
            <DialogContent>
              <p style={{ margin: "0 0 16px", fontSize: 14, color: "#555", lineHeight: 1.5 }}>
                {renamingCategory ? (
                  <>
                    Students see this name as a heading on the website. Its concerns stay
                    as they are - only the name changes.
                  </>
                ) : (
                  <>
                    A category is a group of similar concerns, for example{" "}
                    <b>SOCIAL CONCERNS</b> or <b>ACADEMIC CONCERNS</b>. Students see it as a
                    heading on the website when they pick their concerns.
                  </>
                )}
              </p>
              <TextField
                autoFocus
                fullWidth
                label="Category name"
                placeholder="e.g. FAMILY CONCERNS"
                value={newCategory}
                error={Boolean(categoryError)}
                helperText={
                  categoryError ||
                  (renamingCategory
                    ? "Type the new name and press Save."
                    : "After this, you can add concerns to it in the form above.")
                }
                onChange={(e) => {
                  setNewCategory(e.target.value);
                  setCategoryError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && saveCategory()}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setCategoryDialogOpen(false)} disabled={addingCategory}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveCategory}
                disabled={addingCategory}
                style={{ backgroundColor: "#313E6C" }}
              >
                {addingCategory ? "Saving…" : renamingCategory ? "Save" : "Add Category"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={Boolean(toast)}
            autoHideDuration={5000}
            onClose={() => setToast("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert severity="success" variant="filled" onClose={() => setToast("")}>
              {toast}
            </Alert>
          </Snackbar>

          {categories.map((cat) => (
            <Box
              key={cat.id}
              sx={{ border: "1px solid #eef0f4", borderRadius: "10px", p: 2, mb: 2 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <strong style={{ color: "#313E6C" }}>
                  {cat.category}{" "}
                  <span style={{ color: "#999", fontWeight: 400, fontSize: 13 }}>
                    ({(cat.concerns || []).length})
                  </span>
                </strong>
                <div style={{ display: "flex", gap: 4 }}>
                  <Button size="small" onClick={() => openCategoryDialog(cat)}>
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => deleteCategory(cat)}>
                    Delete category
                  </Button>
                </div>
              </div>

              {(cat.concerns || []).map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 0",
                    borderTop: "1px solid #f3f4f6",
                    fontSize: 14,
                  }}
                >
                  <div>
                    {c.concern}
                    {c.name_for_report && c.name_for_report !== c.concern && (
                      <span style={{ color: "#696984" }}> → {c.name_for_report}</span>
                    )}
                    {!c.for_report && (
                      <span style={{ color: "#b45309", fontSize: 12 }}> · hidden in report</span>
                    )}
                    {!c.practical_strategies && (
                      <span style={{ color: "#dc2626", fontSize: 12 }}> · no strategies</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Button size="small" onClick={() => editConcern(c)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => deleteConcern(c)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </Box>
          ))}

          {!categories.length && (
            <p style={{ color: "#999", fontSize: 14 }}>No concern categories yet.</p>
          )}
        </Box>
      </div>
    </div>
  );
};

export default Concerns;
